import type { Theme } from '@mui/material/styles'
import { createTheme as createMuiTheme } from '@mui/material/styles'
import { MuiAlert, MuiAlertTitle } from './components/alert'
import { MuiAppBar, MuiToolbar } from './components/appBar'
import { MuiBadge } from './components/badge'
import {
  MuiBottomNavigation,
  MuiBottomNavigationAction,
} from './components/bottomNavigation'
import { MuiButton, MuiIconButton } from './components/button'
import { MuiCheckbox } from './components/checkbox'
import { MuiCircularProgress } from './components/circularProgress'
import { MuiBackdrop, MuiDialog, MuiDialogTitle } from './components/dialog'
import { MuiDivider } from './components/divider'
import { MuiDrawer } from './components/drawer'
import { MuiFormControlLabel } from './components/formControlLabel'
import { MuiLinearProgress } from './components/linearProgress'
import {
  MuiList,
  MuiListItem,
  MuiListItemIcon,
  MuiListItemText,
} from './components/list'
import { MuiMenu, MuiMenuItem } from './components/menu'
import { MuiModal } from './components/modal'
import { MuiPaper } from './components/paper'
import { MuiPopover } from './components/popover'
import { MuiRadio } from './components/radio'
import { MuiSelect } from './components/select'
import { MuiSlider } from './components/slider'
import { MuiSnackbar, MuiSnackbarContent } from './components/snackbar'
import { MuiToast,MuiToastContent } from './components/toast'
import { MuiToastAlert, MuiToastAlertTitle } from './components/toastAlert'
import { MuiSvgIcon } from './components/svgIcon'
import { MuiSwitch } from './components/switch'
import { MuiTab } from './components/tab'
import {
  MuiPaginationItem,
  MuiTableBody,
  MuiTableCell,
  MuiTablePagination,
  MuiTableRow,
  MuiTableSortLabel,
} from './components/table'
import {
  MuiFormHelperText,
  MuiFormLabel,
  MuiInputAdornment,
  MuiInputBase,
  MuiInputLabel,
  MuiOutlinedInput,
  MuiTextField,
} from './components/textField'
import { createUseMediaQuery } from './components/useMediaQuery'
import { MuiCssBaseline } from './system/baseline'
import { themeModeToElevations } from './system/elevations'
import { themeModeToPalette } from './system/palette'
import { typography } from './system/typography'
import type { ThemeModes } from './types'


type ThemeUniqueKey = `${ThemeModes | undefined}:${string | undefined}`
const THEME_CACHE: Record<ThemeUniqueKey, Theme> = {}

// Create a theme instance.
export const createTheme = (mode?: ThemeModes, device?: string) => {
  const themeUniqueKey: ThemeUniqueKey = `${mode}:${device}`
  if (THEME_CACHE.hasOwnProperty(themeUniqueKey)) {
    return THEME_CACHE[themeUniqueKey]
  }

  const initialTheme = createMuiTheme({
    typography,
    direction: 'rtl',
    palette: themeModeToPalette[mode || 'light'],
    elevations: themeModeToElevations[mode || 'light'],
    spacing__Wallfund:{
      none: "0px",
      xxxxs: "1px",
      xxxs: "2px",
      xxs: "4px",
      xxsNudge: "6px",
      xs: "8px",
      xsNudge: "10px",
      s:"12px",
      m: "16px",
      l: "20px",
      xl: "24px",
      xxl: "28px",
      xxxl: "32px",
      xxxxl: "36px",
      xxxxxl: "40px",
      xxxxxxl: "44px",
      xxxxxxxl: "48px",
    },
    cornerRadius__Wallfund :{
      none:"0px",
      xs:"2px",
      s:"4px",
      sNudge:"6px",
      m:"8px",
      mNudge:"10px",
      l:"12px",
      xl:"16px",
      circular:"1000px",
     
    },
    color__Wallfund :{
      primaryMain:"#052B61",
      textTertiary:"#8D9FB9",
      textSecondary:'#476185',
      linePrimary:"#ECF1F9",
      lineSecondary:"#BCC9DC",
    }
  })
  // @ts-ignore
  const createdTheme = createMuiTheme(initialTheme, {
    components: {
      MuiCssBaseline: MuiCssBaseline(initialTheme),
      MuiButton: MuiButton(initialTheme),
      MuiIconButton: MuiIconButton(initialTheme),
      MuiAppBar: MuiAppBar(initialTheme),
      MuiToolbar: MuiToolbar(initialTheme),
      MuiSnackbar:MuiToast,
      MuiSnackbarContent: MuiToastContent(initialTheme),
      MuiToast,
      MuiToastContent: MuiToastContent(initialTheme),
      MuiSvgIcon: MuiSvgIcon(initialTheme),
      MuiTextField: MuiTextField(initialTheme),
      MuiInputLabel: MuiInputLabel(initialTheme),
      MuiFormLabel,
      MuiOutlinedInput: MuiOutlinedInput(initialTheme),
      MuiInputAdornment: MuiInputAdornment(initialTheme),
      MuiFormHelperText: MuiFormHelperText(initialTheme),
      MuiModal,
      MuiSelect: MuiSelect(initialTheme),
      MuiMenu: MuiMenu(initialTheme),
      MuiMenuItem: MuiMenuItem(initialTheme),
      MuiAlert: MuiToastAlert(initialTheme),
      MuiAlertTitle: MuiToastAlertTitle(initialTheme),
      MuiToastAlert: MuiToastAlert(initialTheme),
      MuiToastAlertTitle: MuiToastAlertTitle(initialTheme),
      MuiPaper: MuiPaper(initialTheme),
      MuiCheckbox,
      MuiFormControlLabel: MuiFormControlLabel(initialTheme),
      MuiLinearProgress: MuiLinearProgress(initialTheme),
      MuiBottomNavigation: MuiBottomNavigation(initialTheme),
      MuiBottomNavigationAction: MuiBottomNavigationAction(initialTheme),
      MuiTableCell: MuiTableCell(initialTheme),
      MuiTableRow,
      MuiTableSortLabel: MuiTableSortLabel(initialTheme),
      MuiTablePagination: MuiTablePagination(initialTheme),
      MuiPaginationItem: MuiPaginationItem(initialTheme),
      MuiTableBody: MuiTableBody(initialTheme),
      MuiDialog: MuiDialog(initialTheme),
      MuiDialogTitle: MuiDialogTitle(initialTheme),
      MuiBackdrop: MuiBackdrop(initialTheme),
      MuiList,
      MuiListItem,
      MuiListItemIcon: MuiListItemIcon(initialTheme),
      MuiListItemText: MuiListItemText(initialTheme),
      MuiDivider: MuiDivider(initialTheme),
      MuiSlider: MuiSlider(initialTheme),
      MuiBadge: MuiBadge(initialTheme),
      MuiUseMediaQuery: createUseMediaQuery(initialTheme, device),
      MuiInputBase: MuiInputBase(initialTheme),
      MuiSwitch,
      MuiTab: MuiTab(initialTheme),
      MuiRadio,
      MuiCircularProgress,
      MuiPopover,
      MuiDrawer,
    },
  })

  THEME_CACHE[themeUniqueKey] = createdTheme
  return createdTheme
}

export const theme = createTheme()

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  console.log(
    '%cTheme',
    'color:#fff;background:crimson;padding:2px 4px;border-radius:2px',
    theme,
  )
}
